import React, { useMemo, useRef, useEffect, useCallback, useState } from 'react';
import { useLanguage } from '../contexts/LanguageContext';
import { translations } from '../translations/translations';
import useScrollAnimation from '../hooks/useScrollAnimation';
import '../styles/scrollAnimations.css';
import './WhyNow.css';

const generateRainDrops = () => {
  const drops = [];
  let increment = 0;
  while (increment < 100) {
    const randoHundo = Math.floor(Math.random() * 98) + 1;
    const randoFiver = Math.floor(Math.random() * 4) + 2;
    increment += randoFiver;
    drops.push({
      left: `${increment}%`,
      bottom: `${randoFiver * 2 - 1 + 100}%`,
      animDelay: `0.${randoHundo}s`,
      animDuration: `2.5${randoHundo}s`,
    });
  }
  return drops;
};

const generateBackDrops = () => {
  const drops = [];
  let increment = 0;
  while (increment < 100) {
    const randoHundo = Math.floor(Math.random() * 98) + 1;
    const randoFiver = Math.floor(Math.random() * 4) + 2;
    increment += randoFiver;
    drops.push({
      right: `${increment}%`,
      bottom: `${randoFiver * 2 - 1 + 100}%`,
      animDelay: `0.${randoHundo}s`,
      animDuration: `2.5${randoHundo}s`,
    });
  }
  return drops;
};

const STAR_COUNT = 80;
const generateStars = () => {
  const stars = [];
  for (let i = 0; i < STAR_COUNT; i++) {
    const x = Math.random() * 100;
    const y = Math.random() * 100;
    const isStatic = Math.random() < 0.3;
    const speed = isStatic ? 0 : 0.2 + Math.random() * 0.6;
    const size = isStatic ? 1 + Math.random() : 1 + Math.random() * 2;
    stars.push({
      id: i,
      x,
      y,
      initialY: y,
      speed,
      size,
      duration: 2 + Math.random() * 4,
      delay: Math.random() * 5,
    });
  }
  return stars;
};

const WhyNow = () => {
  const { language } = useLanguage();
  const t = translations[language].whyNow;
  const [headerRef, headerVisible] = useScrollAnimation({ threshold: 0.2 });
  const [windowRef, windowVisible] = useScrollAnimation({ threshold: 0.3 });
  const [pointsRef, pointsVisible] = useScrollAnimation({ threshold: 0.1 });

  const frontDrops = useMemo(() => generateRainDrops(), []);
  const backDrops = useMemo(() => generateBackDrops(), []);
  const stars = useMemo(() => generateStars(), []);

  const sectionRef = useRef(null);
  const starContainerRef = useRef(null);
  const rainFrontRef = useRef(null);
  const rainBackRef = useRef(null);
  const lastFlashY = useRef(0);
  const lastScrollY = useRef(0);
  const cooldownRef = useRef(false);
  const [lightning, setLightning] = useState(false);
  const lightningPosRef = useRef({ x: 50, side: 'left' });
  const [rainOpacity, setRainOpacity] = useState(1);

  const triggerLightning = useCallback(() => {
    if (cooldownRef.current) return;
    cooldownRef.current = true;
    lightningPosRef.current = {
      x: 15 + Math.random() * 70,
      side: Math.random() > 0.5 ? 'left' : 'right',
    };
    setLightning(true);
    setTimeout(() => setLightning(false), 700);
    setTimeout(() => { cooldownRef.current = false; }, 2500);
  }, []);

  useEffect(() => {
    const onScroll = () => {
      if (!sectionRef.current) return;
      const rect = sectionRef.current.getBoundingClientRect();
      const vh = window.innerHeight;
      if (rect.top < vh && rect.bottom > 0) {
        const scrolledInSection = vh - rect.top;
        if (scrolledInSection - lastFlashY.current > 600) {
          lastFlashY.current = scrolledInSection;
          triggerLightning();
        }
      }
    };
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, [triggerLightning]);

  // Rain fade-out + star warp: rain on top, stars behind; scroll through section fades rain, moves stars
  useEffect(() => {
    const section = sectionRef.current;
    const starContainer = starContainerRef.current;
    if (!section || !starContainer) return;

    const starEls = starContainer.querySelectorAll('.wnow-star');
    const starConfigs = stars;

    const onScrollStars = () => {
      const rect = section.getBoundingClientRect();
      const sectionTop = section.offsetTop;
      const sectionHeight = section.offsetHeight;
      const scrollY = window.scrollY;
      const vh = window.innerHeight;

      // Rain opacity: 1 at section start -> 0 as you scroll through (rain stops, stars only)
      const scrollProgressInSection = (scrollY - sectionTop + vh * 0.5) / (sectionHeight + vh * 0.5);
      const rainOpacityValue = Math.max(0, Math.min(1, 1 - scrollProgressInSection * 2.2));
      setRainOpacity(rainOpacityValue);
      if (rainFrontRef.current) rainFrontRef.current.style.opacity = rainOpacityValue;
      if (rainBackRef.current) rainBackRef.current.style.opacity = rainOpacityValue * 0.65;

      // Star warp: move stars up with scroll (only when section in view)
      if (rect.top < vh && rect.bottom > 0) {
        const starScroll = Math.max(0, scrollY - sectionTop);
        const velocity = Math.abs(scrollY - lastScrollY.current);
        lastScrollY.current = scrollY;
        const stretch = Math.max(1, Math.min(1 + velocity * 0.08, 4));

        starEls.forEach((el, i) => {
          const cfg = starConfigs[i];
          if (!cfg) return;
          if (cfg.speed === 0) {
            el.style.transform = 'translateX(-50%) scaleY(1)';
            return;
          }
          let pos = (cfg.initialY - starScroll * cfg.speed * 0.05) % 100;
          if (pos < 0) pos += 100;
          el.style.top = pos + '%';
          el.style.transform = `translateX(-50%) scaleY(${stretch})`;
        });
      }
    };

    onScrollStars();
    window.addEventListener('scroll', onScrollStars, { passive: true });
    return () => window.removeEventListener('scroll', onScrollStars);
  }, [stars]);

  return (
    <section ref={sectionRef} className="wnow-section">
      <div className={`wnow-lightning-flash ${lightning ? 'wnow-lightning-active' : ''}`} />
      <svg
        className={`wnow-lightning-bolt ${lightning ? 'wnow-bolt-active' : ''}`}
        style={{ left: `${lightningPosRef.current.x}%` }}
        viewBox="0 0 60 200"
        preserveAspectRatio="none"
      >
        <path
          d="M30 0 L22 70 L38 65 L18 140 L35 130 L10 200 L28 120 L12 128 L32 58 L16 62 Z"
          fill="rgba(200, 220, 255, 0.6)"
          filter="url(#wnow-bolt-glow)"
        />
        <defs>
          <filter id="wnow-bolt-glow" x="-50%" y="-20%" width="200%" height="140%">
            <feGaussianBlur stdDeviation="4" result="blur" />
            <feMerge>
              <feMergeNode in="blur" />
              <feMergeNode in="SourceGraphic" />
            </feMerge>
          </filter>
        </defs>
      </svg>
      <div ref={starContainerRef} className="wnow-stars" aria-hidden="true">
        {stars.map((star) => (
          <div
            key={star.id}
            className="wnow-star"
            style={{
              left: star.x + '%',
              top: star.y + '%',
              width: star.size,
              height: star.size,
              '--wnow-star-duration': star.duration + 's',
              animationDelay: star.delay + 's',
            }}
          />
        ))}
      </div>
      <div ref={rainFrontRef} className="wnow-rain wnow-rain-front" style={{ opacity: rainOpacity }}>
        {frontDrops.map((drop, i) => (
          <div
            key={i}
            className="wnow-drop"
            style={{
              left: drop.left,
              bottom: drop.bottom,
              animationDelay: drop.animDelay,
              animationDuration: drop.animDuration,
            }}
          >
            <div
              className="wnow-stem"
              style={{ animationDelay: drop.animDelay, animationDuration: drop.animDuration }}
            />
            <div
              className="wnow-splat"
              style={{ animationDelay: drop.animDelay, animationDuration: drop.animDuration }}
            />
          </div>
        ))}
      </div>
      <div ref={rainBackRef} className="wnow-rain wnow-rain-back" style={{ opacity: rainOpacity * 0.65 }}>
        {backDrops.map((drop, i) => (
          <div
            key={i}
            className="wnow-drop"
            style={{
              right: drop.right,
              bottom: drop.bottom,
              animationDelay: drop.animDelay,
              animationDuration: drop.animDuration,
            }}
          >
            <div
              className="wnow-stem"
              style={{ animationDelay: drop.animDelay, animationDuration: drop.animDuration }}
            />
            <div
              className="wnow-splat"
              style={{ animationDelay: drop.animDelay, animationDuration: drop.animDuration }}
            />
          </div>
        ))}
      </div>
      <div className="wnow-ambient" />
      <div className="wnow-inner">
        <div
          ref={headerRef}
          className={`wnow-header scroll-fade-in ${headerVisible ? 'is-visible' : ''}`}
        >
          <span className="section-label">{t.label}</span>
          <h2 className="wnow-title">{t.title}</h2>
        </div>

        <div
          ref={windowRef}
          className={`wnow-window ${windowVisible ? 'wnow-window-visible' : ''}`}
        >
          <div className="wnow-window-glow" />
          <div className="wnow-window-glass-frame">
            <div className="wnow-window-crossbar-h" />
            <div className="wnow-window-crossbar-v" />
            <div className="wnow-window-light" />
            <div className="wnow-glass-reflection" />
            <div className="wnow-shutter wnow-shutter-left">
              <div className="wnow-handle wnow-handle-left" />
            </div>
            <div className="wnow-shutter wnow-shutter-right">
              <div className="wnow-handle wnow-handle-right" />
            </div>
          </div>
          <div className="wnow-window-sill" />
        </div>

        {t.subtitle && <p className="wnow-subtitle">{t.subtitle}</p>}

        {t.floatingCards && (
          <div className="wnow-constellation">
            <h3 className="wnow-constellation-headline">{t.floatingHeadline}</h3>
            <div className="wnow-constellation-field">
              {t.floatingCards.map((card, i) => (
                <div
                  key={i}
                  className={`wnow-constellation-card wnow-cc-${i}`}
                  style={{ animationDelay: `${i * 1.1}s` }}
                >
                  <div className="wnow-cc-glow" />
                  <span className="wnow-cc-star" />
                  <div className="wnow-cc-line" />
                  <div className="wnow-cc-body">
                    <h4>{card.title}</h4>
                    <p>{card.text}</p>
                  </div>
                </div>
              ))}
              <svg className="wnow-constellation-lines" viewBox="0 0 800 600" preserveAspectRatio="none">
                <line x1="120" y1="80" x2="620" y2="160" stroke="rgba(255,255,255,0.04)" strokeWidth="1" />
                <line x1="620" y1="160" x2="200" y2="310" stroke="rgba(255,255,255,0.04)" strokeWidth="1" />
                <line x1="200" y1="310" x2="580" y2="420" stroke="rgba(255,255,255,0.04)" strokeWidth="1" />
                <line x1="580" y1="420" x2="140" y2="540" stroke="rgba(255,255,255,0.04)" strokeWidth="1" />
              </svg>
            </div>
          </div>
        )}

        <div
          ref={pointsRef}
          className="wnow-points wnow-points--hidden"
        >
          {t.points.map((point, i) => (
            <div
              key={i}
              className={`wnow-point-wrapper scroll-fade-in-stagger ${pointsVisible ? 'is-visible' : ''}`}
            >
              {point.reason && <span className="wnow-point-reason">{point.reason}</span>}
              <div className="wnow-point-stack" style={{ '--reveal-delay': `${i * 0.12}s` }}>
                <div className="wnow-point-image">
                  {point.image
                    ? <img src={point.image} alt={point.titleCursive} />
                    : <div className="wnow-point-image-placeholder"><i className={point.icon} /></div>
                  }
                </div>
                <div className="wnow-point">
                  <div className="wnow-point-body">
                    <h3 className="wnow-point-title">
                      <span className="wnow-point-title-cursive">{point.titleCursive}</span>{' '}
                      <span className="wnow-point-title-clean">{point.titleClean}</span>
                    </h3>
                    <p className="wnow-point-text">{point.text}</p>
                    <span className="wnow-point-source">{point.source}</span>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>

      </div>
    </section>
  );
};

export default WhyNow;
